import { useCallback, useEffect, useMemo, useState } from 'react'
import { ID } from '@/types/common'
import {
  createTemplateRow,
  defaultClassRecordRules,
  GenerateTemplateItem,
  toDraftTemplate,
} from '../page-types'
import {
  BillingMode,
  ClassType,
  ClassRecordRules,
  StudentClassCurrentConfig,
} from '../types'

export function useClassRecordConfigDraft(
  currentConfig: StudentClassCurrentConfig | null,
  rules?: ClassRecordRules | null,
) {
  const effectiveRules = rules ?? defaultClassRecordRules()
  const [configDraftWeekdays, setConfigDraftWeekdays] = useState<number[]>([])
  const [configDraftIsAllDayPricing, setConfigDraftIsAllDayPricing] = useState(false)
  const [configDraftMonthlyPrice, setConfigDraftMonthlyPrice] = useState<
    number | undefined
  >(undefined)
  const [configDraftTemplates, setConfigDraftTemplates] = useState<
    GenerateTemplateItem[]
  >([createTemplateRow(effectiveRules.defaultCoursePrice)])

  const resetConfigDraft = useCallback(() => {
    setConfigDraftWeekdays([])
    setConfigDraftIsAllDayPricing(false)
    setConfigDraftMonthlyPrice(undefined)
    setConfigDraftTemplates([createTemplateRow(effectiveRules.defaultCoursePrice)])
  }, [effectiveRules.defaultCoursePrice])

  const hydrateFromConfig = useCallback(
    (config: StudentClassCurrentConfig | null) => {
      if (!config) {
        resetConfigDraft()
        return
      }

      setConfigDraftWeekdays(config.weekdays || [])
      setConfigDraftIsAllDayPricing(config.isAllDayPricing ?? false)
      setConfigDraftMonthlyPrice(config.monthlyPrice ?? undefined)
      setConfigDraftTemplates(
        config.templates?.length
          ? config.templates.map((template) =>
              toDraftTemplate(
                template,
                effectiveRules.defaultCoursePrice,
                config.isAllDayPricing ?? false,
              ),
            )
          : [createTemplateRow(effectiveRules.defaultCoursePrice)],
      )
    },
    [effectiveRules.defaultCoursePrice, resetConfigDraft],
  )

  useEffect(() => {
    setConfigDraftTemplates((prev) =>
      prev.map((template) => {
        if (configDraftIsAllDayPricing) {
          return {
            ...template,
            billingMode: BillingMode.ALL_DAY,
            coursePrice: undefined,
          }
        }
        if (template.billingMode !== BillingMode.ALL_DAY) {
          return template
        }
        return {
          ...template,
          billingMode: BillingMode.PER_CLASS,
          coursePrice: undefined,
        }
      }),
    )
  }, [configDraftIsAllDayPricing])

  const normalizeDraftTemplates = useCallback(
    (templates: GenerateTemplateItem[], isAllDayPricing: boolean) =>
      templates.map((template) => ({
        ...template,
        billingMode: isAllDayPricing ? BillingMode.ALL_DAY : template.billingMode,
        coursePrice:
          isAllDayPricing ||
          template.billingMode === BillingMode.GIFT ||
          template.billingMode === BillingMode.ALL_DAY
            ? undefined
            : template.coursePrice,
      })),
    [],
  )

  const getDraftValidationMessage = useCallback(
    (templates: GenerateTemplateItem[]) => {
      if (configDraftIsAllDayPricing) {
        if (
          configDraftMonthlyPrice === undefined ||
          configDraftMonthlyPrice === null ||
          configDraftMonthlyPrice <= 0
        ) {
          return '开启全天班计费后必须填写全天班费用'
        }
      } else if (
        configDraftMonthlyPrice !== undefined &&
        configDraftMonthlyPrice !== null
      ) {
        return '未启用全天班计费时不可填写全天班费用'
      }

      const invalidTemplate = templates.find(
        (template) =>
          (template.classType !== ClassType.GROUP && !template.teacherId) ||
          !template.classType ||
          (!configDraftIsAllDayPricing && !template.billingMode) ||
          (!configDraftIsAllDayPricing &&
            template.billingMode === BillingMode.PER_CLASS &&
            (template.coursePrice === undefined ||
              template.coursePrice === null ||
              template.coursePrice <= 0)) ||
          (!configDraftIsAllDayPricing &&
            template.billingMode === BillingMode.MONTHLY &&
            (template.coursePrice === undefined ||
              template.coursePrice === null ||
              template.coursePrice <= 0)) ||
          (template.coursePrice !== undefined &&
            template.coursePrice !== null &&
            (configDraftIsAllDayPricing ||
              template.billingMode === BillingMode.GIFT ||
              template.billingMode === BillingMode.ALL_DAY)),
      )

      if (!invalidTemplate) {
        return null
      }

      return configDraftIsAllDayPricing
        ? '请完整填写模板：启用全天班计费后，模板老师必填规则保持不变，模板价格必须留空'
        : '请完整填写模板：按节收费填写单价；按月收费填写模板月费；赠课不可填写价格'
    },
    [configDraftIsAllDayPricing, configDraftMonthlyPrice],
  )

  const buildConfigSignature = useCallback(
    (
      weekdays: number[],
      isAllDayPricing: boolean,
      monthlyPrice: number | null | undefined,
      templates: Array<{
        classType: ClassType
        billingMode: BillingMode
        teacherId?: ID | null
        coursePrice?: number | null
      }>,
    ) => {
      const normalizedWeekdays = Array.from(new Set(weekdays)).sort((a, b) => a - b)
      const normalizedMonthlyPrice =
        isAllDayPricing && monthlyPrice !== undefined && monthlyPrice !== null
          ? monthlyPrice
          : null

      const normalizedTemplates = templates
        .map((template) => {
          const billingMode = isAllDayPricing
            ? BillingMode.ALL_DAY
            : template.billingMode
          const teacherId = template.teacherId ?? null
          const rawCoursePrice =
            template.coursePrice === undefined || template.coursePrice === null
              ? null
              : template.coursePrice
          const coursePrice =
            billingMode === BillingMode.GIFT || billingMode === BillingMode.ALL_DAY
              ? null
              : rawCoursePrice
          return {
            classType: template.classType,
            billingMode,
            teacherId,
            coursePrice,
          }
        })
        .sort((a, b) => {
          const left = `${a.classType}|${a.billingMode}|${a.teacherId ?? ''}|${a.coursePrice ?? ''}`
          const right = `${b.classType}|${b.billingMode}|${b.teacherId ?? ''}|${b.coursePrice ?? ''}`
          return left.localeCompare(right)
        })

      return JSON.stringify({
        weekdays: normalizedWeekdays,
        isAllDayPricing,
        monthlyPrice: normalizedMonthlyPrice,
        templates: normalizedTemplates,
      })
    },
    [],
  )

  const isConfigDirty = useMemo(() => {
    if (!currentConfig) {
      return true
    }
    const currentSignature = buildConfigSignature(
      currentConfig.weekdays,
      currentConfig.isAllDayPricing,
      currentConfig.monthlyPrice ?? null,
      currentConfig.templates,
    )
    const draftSignature = buildConfigSignature(
      configDraftWeekdays,
      configDraftIsAllDayPricing,
      configDraftMonthlyPrice,
      normalizeDraftTemplates(configDraftTemplates, configDraftIsAllDayPricing),
    )
    return currentSignature !== draftSignature
  }, [
    buildConfigSignature,
    configDraftIsAllDayPricing,
    configDraftMonthlyPrice,
    configDraftTemplates,
    configDraftWeekdays,
    currentConfig,
    normalizeDraftTemplates,
  ])

  const buildConfigTemplatePayload = useCallback(
    () =>
      normalizeDraftTemplates(configDraftTemplates, configDraftIsAllDayPricing).map(
        (template) => ({
          classType: template.classType,
          billingMode: template.billingMode,
          teacherId: template.teacherId,
          coursePrice: template.coursePrice,
        }),
      ),
    [configDraftIsAllDayPricing, configDraftTemplates, normalizeDraftTemplates],
  )

  const handleConfigDraftTemplateChange = useCallback(
    (localId: string, next: Partial<GenerateTemplateItem>) => {
      setConfigDraftTemplates((prev) =>
        prev.map((template) =>
          template.localId === localId
            ? {
                ...template,
                ...next,
                ...(next.billingMode === BillingMode.GIFT ||
                next.billingMode === BillingMode.ALL_DAY
                  ? { coursePrice: undefined }
                  : {}),
              }
            : template,
        ),
      )
    },
    [],
  )

  const handleConfigDraftAddTemplate = useCallback(() => {
    setConfigDraftTemplates((prev) => [
      ...prev,
      createTemplateRow(effectiveRules.defaultCoursePrice),
    ])
  }, [effectiveRules.defaultCoursePrice])

  const handleConfigDraftRemoveTemplate = useCallback((localId: string) => {
    setConfigDraftTemplates((prev) => {
      if (prev.length <= 1) return prev
      return prev.filter((item) => item.localId !== localId)
    })
  }, [])

  return {
    configDraftWeekdays,
    setConfigDraftWeekdays,
    configDraftIsAllDayPricing,
    setConfigDraftIsAllDayPricing,
    configDraftMonthlyPrice,
    setConfigDraftMonthlyPrice,
    configDraftTemplates,
    getDraftValidationMessage,
    isConfigDirty,
    buildConfigTemplatePayload,
    handleConfigDraftTemplateChange,
    handleConfigDraftAddTemplate,
    handleConfigDraftRemoveTemplate,
    hydrateFromConfig,
    resetConfigDraft,
  }
}
